import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ReadableAccordion';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Star,
  Truck,
  Users,
  Wrench
} from 'lucide-react';
import InlineBookingForm from './InlineBookingForm';
import { HeroProofList } from './HeroProofList';
import { ProofMetric } from './ProofMetric';
import { BrandLogoWithFallback } from './BrandLogoWithFallback';
import { TechnicianNetworkCta } from './TechnicianNetworkCta';
import {
  commonPrinterIssues,
  companyInfo,
  contactInfo,
  conversionImages,
  facilities,
  faqs,
  googleReviewsData,
  howItWorks,
  services,
  supportedBrandsDetailed
} from '../data/mockData';
import { trackEvent } from '../utils/analytics';
import { scrollToBookingForm } from '../utils/bookingFormHighlight';

const serviceTypeByTitle = {
  'In-Store Repair': 'in-store',
  'On-Site Service': 'on-site',
  'Preventative Maintenance': 'maintenance'
};

const serviceBestForLabels = {
  'in-store': 'send-in or drop-off faults',
  'on-site': 'schools and fleets',
  maintenance: 'preventing repeat failures'
};

const serviceIconMap = {
  Wrench,
  Truck,
  Calendar
};

const heroProofItems = ['4.9/5 from 700+ reviews', 'Australia-wide support', 'Fully insured', 'Fully equipped repair centre'];

const issueIntentCopy = (issue) => `I need help with ${issue.title}: ${issue.shortDesc}.`;

export default function ConversionLandingPage() {
  const bookingFormRef = useRef(null);
  const [activeIssue, setActiveIssue] = useState(0);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [formInView, setFormInView] = useState(true);

  const topBrands = useMemo(() => supportedBrandsDetailed.slice(0, 10), []);
  const visibleBrands = showAllBrands ? supportedBrandsDetailed : topBrands;
  const visibleFaqs = faqs;

  useEffect(() => {
    const formNode = bookingFormRef.current;
    if (!formNode) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(formNode);
    return () => observer.disconnect();
  }, []);

  const scrollToBooking = (prefill = {}, tracking = {}) => {
    if (tracking.cta_location) {
      trackEvent('cta_click', {
        cta_location: tracking.cta_location,
        cta_label: tracking.cta_label || 'Start repair request'
      });
    }
    if (Object.keys(prefill).length) {
      setBookingPrefill({ ...prefill, sequence: Date.now() });
    }
    scrollToBookingForm(bookingFormRef);
  };

  const handleIssueRepair = (issue) => {
    trackEvent('printer_issue_repair_click', {
      issue_id: issue.id,
      issue_title: issue.title.toLowerCase().replace(/\s+/g, '_')
    });
    scrollToBooking({
      issueDescription: issueIntentCopy(issue),
      urgency: issue.id === 1 ? 'same-day' : 'priority'
    }, {
      cta_location: 'common_issues',
      cta_label: 'Start repair request'
    });
  };

  const handlePhoneClick = (phoneLocation) => {
    trackEvent('phone_click', { phone_location: phoneLocation });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 pb-20 md:pb-0">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="site-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <a href="https://www.3dprintergear.com.au" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="header-logo-link">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 sm:h-12 w-auto" data-testid="header-logo-image" />
              <div className="hidden sm:block">
                <p className="text-sm font-extrabold tracking-tight" data-testid="header-company-name">{companyInfo.name}</p>
                <p className="text-xs text-gray-500" data-testid="header-company-division">Service &amp; Repair Division</p>
              </div>
            </a>
            <div className="flex items-center gap-3 sm:gap-5">
              <a href="tel:1300334327" onClick={() => handlePhoneClick('header')} className="hidden md:flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black transition-colors" data-testid="header-phone-link">
                <Phone className="h-4 w-4" />
                <span className="leading-tight" data-testid="header-phone-text">{contactInfo.phone} <span className="text-gray-400">·</span> Option 2</span>
              </a>
              <button onClick={() => scrollToBooking({}, { cta_location: 'header', cta_label: 'Book Service' })} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-3 sm:px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors whitespace-nowrap" data-testid="header-book-service-button">
                <span className="sm:hidden">Book</span>
                <span className="hidden sm:inline">Book Service</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white" data-testid="hero-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 lg:py-14">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 items-start">
              <div className="lg:col-span-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-500 mb-3" data-testid="hero-eyebrow">Australia-wide repair specialists</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-4" data-testid="hero-heading">
                  Get your 3D printer repaired by specialists
                </h1>
                <p className="text-base text-gray-600 leading-relaxed max-w-xl mb-5" data-testid="hero-subheading">
                  Bambu Lab, Prusa, Creality, Ultimaker and 30+ brands. Australia-wide service with a free quote before any work begins.
                </p>
                <HeroProofList items={heroProofItems} testId="hero-proof-list" />
                <div className="hidden lg:block border border-gray-200 overflow-hidden rounded-sm" data-testid="hero-repair-centre-image-wrap">
                  <img src={conversionImages.heroRepairTechnician} alt="Bambu Lab X1 Carbon style enclosed 3D printer for specialist repair service" className="w-full aspect-[16/7] object-cover object-center" data-testid="hero-repair-centre-image" />
                </div>
              </div>
              <div className="lg:col-span-7 lg:sticky lg:top-24" data-testid="hero-booking-column">
                <InlineBookingForm key={bookingPrefill?.sequence || 'homepage-booking-form'} ref={bookingFormRef} prefill={bookingPrefill} pageSource="Homepage" />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 text-white" data-testid="conversion-proof-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-700 border border-gray-700">
              {[
                ['700+', 'Google reviews'],
                ['AU-wide', 'Service coverage'],
                ['Insured', 'PI + public liability'],
                ['Centre', 'Fully equipped repair centre'],
                ['12+', 'Years experience']
              ].map(([number, label]) => <ProofMetric key={label} number={number} label={label} testIdPrefix="proof-metric" />)}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="google-reviews-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 mb-4" data-testid="google-rating-summary">
                  <span className="text-2xl font-extrabold">{googleReviewsData.rating}/5</span>
                  <div className="flex" aria-label="Five star rating">
                    {Array.from({ length: 5 }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`summary-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}
                  </div>
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="reviews-heading">Proof from real customers</h2>
                <p className="text-base text-gray-600 mb-5" data-testid="reviews-summary">Fast diagnosis, practical advice and repairs handled by people who work with 3D printers every day.</p>
                <button onClick={() => { trackEvent('cta_click', { cta_location: 'reviews', cta_label: 'See all reviews' }); window.open(googleReviewsData.googleMapsUrl, '_blank'); }} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm transition-colors" data-testid="see-all-reviews-button">
                  See all reviews
                </button>
              </div>
              <div className="lg:col-span-8 grid md:grid-cols-3 gap-4">
                {googleReviewsData.reviews.map((review) => (
                  <article key={review.id} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`google-review-card-${review.id}`}>
                    <div className="flex mb-3" data-testid={`google-review-stars-${review.id}`}>
                      {Array.from({ length: review.rating }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`${review.id}-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4" data-testid={`google-review-content-${review.id}`}>“{review.content}”</p>
                    <p className="text-sm font-bold" data-testid={`google-review-author-${review.id}`}>{review.name}</p>
                    <p className="text-xs text-gray-500" data-testid={`google-review-source-${review.id}`}>Google Review</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="repair-paths-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="repair-paths-heading">Choose the repair path that fits</h2>
                <p className="text-base text-gray-600" data-testid="repair-paths-summary">Send in or drop off your printer, arrange on-site support where available, or plan preventative maintenance for your fleet.</p>
              </div>
              <div className="lg:col-span-8 space-y-3">
                {services.map((service) => {
                  const Icon = serviceIconMap[service.icon] || Wrench;
                  const serviceType = serviceTypeByTitle[service.title] || 'in-store';
                  return (
                    <div key={service.id} className="grid sm:grid-cols-[auto_1fr_auto] gap-4 items-start border border-gray-200 bg-white p-5 rounded-sm" data-testid={`service-path-${serviceType}`}>
                      <Icon className="h-6 w-6 text-gray-900" />
                      <div>
                        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-gray-500 mb-1" data-testid={`service-path-best-for-${serviceType}`}>Best for {serviceBestForLabels[serviceType]}</p>
                        <h3 className="text-lg font-extrabold mb-1" data-testid={`service-path-title-${serviceType}`}>{service.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-testid={`service-path-description-${serviceType}`}>{service.description}</p>
                      </div>
                      <button onClick={() => { trackEvent('repair_path_select', { service_type: serviceType, section_location: 'repair_paths' }); scrollToBooking({ serviceType }, { cta_location: 'repair_paths', cta_label: 'Choose path' }); }} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-4 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors whitespace-nowrap" data-testid={`choose-service-path-${serviceType}`}>
                        Choose path
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#0a0a0a] text-white" data-testid="common-issues-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start mb-8">
              <div className="lg:col-span-5">
                <p className="text-orange-500 font-bold uppercase tracking-[0.18em] text-xs mb-3" data-testid="common-issues-eyebrow">Diagnose the risk</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4" data-testid="common-issues-heading">Common printer failures that need expert repair</h2>
                <p className="text-base text-gray-400 leading-relaxed" data-testid="common-issues-summary">Seeing one of these symptoms? Send it with your repair request and our team can triage faster.</p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-2 gap-3">
                {commonPrinterIssues.map((issue, index) => (
                  <button key={issue.id} onClick={() => { setActiveIssue(index); trackEvent('printer_issue_select', { issue_id: issue.id, issue_title: issue.title.toLowerCase().replace(/\s+/g, '_') }); }} className={`text-left border p-4 rounded-sm transition-colors ${activeIssue === index ? 'bg-[#181818] border-gray-500' : 'bg-[#111111] border-gray-800 hover:border-gray-500'}`} data-testid={`printer-issue-tab-${issue.id}`}>
                    <AlertTriangle className={`h-5 w-5 mb-3 ${activeIssue === index ? 'text-orange-500' : 'text-gray-500'}`} />
                    <p className="text-sm font-extrabold" data-testid={`printer-issue-tab-title-${issue.id}`}>{issue.title}</p>
                    <p className="text-xs text-gray-500 mt-1" data-testid={`printer-issue-tab-summary-${issue.id}`}>{issue.shortDesc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid lg:grid-cols-2 border border-gray-800 rounded-sm overflow-hidden bg-[#141414]" data-testid="issue-detail-panel">
              <div className="bg-black min-h-[280px] lg:min-h-[420px] flex items-center justify-center">
                <img src={commonPrinterIssues[activeIssue].image} alt={commonPrinterIssues[activeIssue].title} className="w-full h-full object-contain" data-testid="active-issue-image" />
              </div>
              <div className="p-6 lg:p-10 flex flex-col justify-center">
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-500 mb-3" data-testid="active-issue-badge">{commonPrinterIssues[activeIssue].badge}</p>
                <h3 className="text-2xl font-extrabold mb-3" data-testid="active-issue-title">{commonPrinterIssues[activeIssue].title}</h3>
                <p className="text-gray-400 leading-relaxed mb-5" data-testid="active-issue-description">{commonPrinterIssues[activeIssue].description}</p>
                <ul className="space-y-3 mb-6" data-testid="active-issue-points">
                  {commonPrinterIssues[activeIssue].bulletPoints.map((point, index) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-gray-300" data-testid={`active-issue-point-${index}`}>
                      <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleIssueRepair(commonPrinterIssues[activeIssue])} className="w-fit bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold px-5 py-3 rounded-sm transition-colors" data-testid="active-issue-repair-button">
                  Start repair request <ArrowRight className="inline ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="facilities-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="facilities-heading">Real repair facilities, real technicians</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-4" data-testid="facilities-summary">Credibility matters when a printer is critical to a school, business or production workflow. Our repair centre supports drop-off, send-in, remote triage and scheduled on-site service Australia-wide.</p>
                <div className="border border-gray-200 bg-gray-50 px-4 py-3 mb-5 rounded-sm text-sm text-gray-700" data-testid="repair-centre-contact-card">
                  <p className="font-extrabold text-gray-900 mb-1" data-testid="repair-centre-card-title">Repair-centre backed workflow</p>
                  <p className="leading-relaxed" data-testid="repair-centre-card-copy">Australia-wide repair requests start with printer details, symptoms and photos so we can confirm the best service path before work begins.</p>
                  <a href="tel:1300334327" onClick={() => handlePhoneClick('facilities')} className="inline-flex items-center gap-1.5 mt-2 font-semibold text-gray-900 hover:text-black" data-testid="repair-centre-card-phone-link">
                    <Phone className="h-3.5 w-3.5 text-orange-500" />
                    <span data-testid="repair-centre-card-phone-text">{contactInfo.phone} · Option 2</span>
                  </a>
                </div>
                <button onClick={() => scrollToBooking({}, { cta_location: 'facilities', cta_label: 'Book a repair' })} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="facilities-book-button">
                  Book a repair
                </button>
              </div>
              <div className="lg:col-span-8 grid md:grid-cols-2 gap-5">
                {[
                  ['service-centre', facilities.serviceCentre],
                  ['training-centre', facilities.trainingCentre]
                ].map(([key, facility]) => (
                  <article key={key} className="border border-gray-200 rounded-sm overflow-hidden" data-testid={`${key}-facility-card`}>
                    <img src={facility.image} alt={facility.title} className="w-full aspect-[4/3] object-cover" data-testid={`${key}-facility-image`} />
                    <div className="p-5">
                      <h3 className="text-lg font-extrabold mb-2" data-testid={`${key}-facility-title`}>{facility.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-testid={`${key}-facility-description`}>{facility.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="supported-brands-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2" data-testid="brands-heading">Brands and models we service</h2>
                <p className="text-base text-gray-600" data-testid="brands-summary">Top brands shown first. We service 30+ brands and custom machines.</p>
              </div>
              <button onClick={() => { const nextValue = !showAllBrands; setShowAllBrands(nextValue); trackEvent(nextValue ? 'brands_expand' : 'brands_collapse', { section_location: 'supported_brands' }); }} className="self-start bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm transition-colors" data-testid="toggle-all-brands-button">
                {showAllBrands ? 'Show top brands' : 'See all supported brands'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-gray-200 border border-gray-200" data-testid="brands-grid">
              {visibleBrands.map((brandInfo) => (
                <div key={brandInfo.brand} className="bg-white min-h-[132px] p-4 flex flex-col justify-between" data-testid={`brand-card-${brandInfo.brand.toLowerCase().replace(/\s+/g, '-')}`}>
                  <BrandLogoWithFallback brand={brandInfo.brand} logo={brandInfo.logo} logoDarkBg={brandInfo.logoDarkBg} size="compact" testId={`brand-logo-${brandInfo.brand.toLowerCase().replace(/\s+/g, '-')}`} />
                  <div className="mt-3">
                    <p className="text-sm font-extrabold" data-testid={`brand-name-${brandInfo.brand.toLowerCase().replace(/\s+/g, '-')}`}>{brandInfo.brand}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2" data-testid={`brand-models-${brandInfo.brand.toLowerCase().replace(/\s+/g, '-')}`}>{brandInfo.models.slice(0, 5).join(', ')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="how-it-works-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="how-it-works-heading">How repair requests move</h2>
            <p className="text-base text-gray-600 mb-8" data-testid="how-it-works-summary">A clear process from first request to tested printer.</p>
            <div className="grid md:grid-cols-5 gap-px bg-gray-200 border border-gray-200" data-testid="how-it-works-grid">
              {howItWorks.map((step) => (
                <article key={step.step} className="bg-white p-5" data-testid={`process-step-${step.step}`}>
                  <p className="text-3xl font-extrabold mb-4" data-testid={`process-step-number-${step.step}`}>{step.step}</p>
                  <h3 className="text-base font-extrabold mb-2" data-testid={`process-step-title-${step.step}`}>{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-testid={`process-step-description-${step.step}`}>{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="institutional-trust-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="institutional-trust-heading">Built for schools, businesses and serious makers</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="institutional-trust-summary">When printer downtime affects classes, orders or deadlines, the repair experience needs to be safe, documented, insured and predictable.</p>
                <div className="mt-6 border border-gray-200 rounded-sm overflow-hidden bg-white" data-testid="school-fleet-image-wrap">
                  <img src={conversionImages.schoolFleetService} alt="Technician servicing multiple 3D printers in an education workshop" className="w-full aspect-[4/3] object-cover object-center" data-testid="school-fleet-service-image" />
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                {[
                  [ShieldCheck, 'WWCC & Police cleared', 'Safe for school and education environments.'],
                  [CheckCircle, 'Fully insured', 'Professional indemnity and public liability cover.'],
                  [Building2, 'Fully equipped centre', 'Diagnostics, parts and specialist tools.'],
                  [Truck, 'Australia-wide service', 'Support by drop-off, send-in, remote triage or on-site arrangement.'],
                  [Users, 'Fleet-ready support', 'Multiple machines can be captured in one request.'],
                  [CheckCircle, 'Quote-before-work', 'No repair begins until the quote is clear.']
                ].map(([Icon, title, copy]) => (
                  <div key={title} className="border border-gray-200 bg-white p-5 rounded-sm" data-testid={`trust-card-${title.toLowerCase().replace(/\s|&/g, '-')}`}>
                    <Icon className="h-6 w-6 mb-3 text-gray-900" />
                    <h3 className="text-base font-extrabold mb-1" data-testid={`trust-card-title-${title.toLowerCase().replace(/\s|&/g, '-')}`}>{title}</h3>
                    <p className="text-sm text-gray-600" data-testid={`trust-card-copy-${title.toLowerCase().replace(/\s|&/g, '-')}`}>{copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="faq-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="faq-heading">Questions before booking</h2>
                <p className="text-base text-gray-600 mb-5" data-testid="faq-summary">Repair costs, service options, turnaround, parts and preparation—answers from the 3D Printer Gear team.</p>
              </div>
              <div className="lg:col-span-8">
                <Accordion type="single" collapsible className="border-t border-gray-200" data-testid="faq-accordion" onValueChange={(value) => {
                  if (!value) return;
                  const faqId = Number(value.replace('faq-', ''));
                  const faq = visibleFaqs.find((item) => item.id === faqId);
                  if (!faq) return;
                  trackEvent('faq_open', {
                    faq_id: faq.id,
                    faq_category: faq.category
                  });
                }}>
                  {visibleFaqs.map((faq) => (
                    <AccordionItem key={faq.id} value={`faq-${faq.id}`} className="border-b border-gray-200" data-testid={`faq-item-${faq.id}`}>
                      <AccordionTrigger className="text-left py-4 hover:no-underline" data-testid={`faq-question-${faq.id}`}>
                        <span className="text-sm font-extrabold text-gray-900 pr-4">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5" data-testid={`faq-answer-${faq.id}`}>
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <TechnicianNetworkCta location="homepage" />

        <section className="py-14 bg-gray-900 text-white" data-testid="final-cta-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="final-cta-heading">Ready to get your printer fixed?</h2>
            <p className="text-base text-gray-400 mb-6" data-testid="final-cta-summary">Start the repair request and our team will come back with the next step.</p>
            <button onClick={() => scrollToBooking({}, { cta_location: 'final_cta', cta_label: 'Start repair request' })} className="bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-6 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="final-cta-book-button">
              Start repair request
            </button>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white py-14 border-t border-gray-800" data-testid="site-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto filter brightness-0 invert mb-4" data-testid="footer-logo" />
              <p className="text-gray-400 text-sm leading-relaxed" data-testid="footer-description">Australia&apos;s trusted 3D printer service and repair team.</p>
              <div className="mt-4 space-y-2">
                <Link to="/3d-printer-repair-melbourne" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-melbourne-seo-link">3D Printer Repair Melbourne</Link>
                <Link to="/3d-printer-repair-sydney" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-sydney-seo-link">3D Printer Repair Sydney</Link>
                <Link to="/3d-printer-repair-brisbane" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-brisbane-seo-link">3D Printer Repair Brisbane</Link>
                <Link to="/3d-printer-repair-perth" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-perth-seo-link">3D Printer Repair Perth</Link>
                <Link to="/3d-printer-repair-adelaide" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-adelaide-seo-link">3D Printer Repair Adelaide</Link>
                <Link to="/bambu-lab-repair-australia" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-bambu-lab-seo-link">Bambu Lab Repair Australia</Link>
                <Link to="/3d-printer-blob-of-death-repair" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-blob-of-death-seo-link">Blob of Death Repair</Link>
                <Link to="/3d-printer-nozzle-clog-repair" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-nozzle-clog-seo-link">Nozzle Clog Repair</Link>
                <Link to="/3d-printer-layer-shifting-repair" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-layer-shifting-seo-link">Layer Shifting Repair</Link>
                <Link to="/3d-printer-bed-adhesion-repair" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-bed-adhesion-seo-link">Bed Adhesion Repair</Link>
                <Link to="/3d-printer-under-extrusion-repair" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-under-extrusion-seo-link">Under-Extrusion Repair</Link>
                <Link to="/3d-printer-repairs-for-schools" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-school-repairs-seo-link">3D Printer Repairs for Schools</Link>
                <Link to="/3d-printer-maintenance-for-schools" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-school-maintenance-seo-link">3D Printer Maintenance for Schools</Link>
                <Link to="/apply-as-technician" className="block text-sm font-semibold text-gray-300 hover:text-white" data-testid="footer-apply-as-technician-link">Apply as a Technician</Link>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="footer-contact-heading">Contact</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <p className="flex items-start gap-3" data-testid="footer-address"><MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />{contactInfo.address}</p>
                <a href="tel:1300334327" onClick={() => handlePhoneClick('footer')} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone-link"><Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />{contactInfo.phone} ({contactInfo.phoneOption})</a>
                <a href="mailto:admin@3dprintergear.com.au" onClick={() => trackEvent('email_click', { email_location: 'footer' })} className="flex items-center gap-3 hover:text-white" data-testid="footer-email-link"><Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />admin@3dprintergear.com.au</a>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="footer-hours-heading">Hours</h3>
              <div className="text-sm text-gray-300 space-y-1 mb-5" data-testid="footer-business-hours">
                <p>{contactInfo.businessHours.weekdays}</p>
                <p>{contactInfo.businessHours.saturday}</p>
                <p>{contactInfo.businessHours.sunday}</p>
              </div>
              <button onClick={() => scrollToBooking({}, { cta_location: 'footer', cta_label: 'Book Service' })} className="w-full bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="footer-book-button">
                Book Service
              </button>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-10 pt-6 text-sm text-gray-500" data-testid="footer-copyright">
            {contactInfo.copyright} {companyInfo.name}. All rights reserved. | {contactInfo.abn} | <a href="https://www.3dprintergear.com.au/privacy-policy/" className="hover:text-gray-300 underline underline-offset-2" data-testid="footer-privacy-policy-link">Privacy Policy</a>
          </div>
        </div>
      </footer>

      {!formInView && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-3" data-testid="mobile-sticky-cta-wrap">
          <div className="grid grid-cols-[1fr_auto] gap-2">
              <button onClick={() => scrollToBooking({}, { cta_location: 'mobile_sticky', cta_label: 'Start Repair Form' })} className="bg-gray-900 hover:bg-black text-white text-sm font-extrabold py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="mobile-sticky-start-form-button">
              Start Repair Form
            </button>
            <a href="tel:1300334327" onClick={() => handlePhoneClick('mobile_sticky')} className="border border-gray-300 text-gray-900 text-sm font-extrabold px-4 py-3 rounded-sm" data-testid="mobile-sticky-call-button">
              Call Option 2
            </a>
          </div>
        </div>
      )}
    </div>
  );
}